/**
 * Desktop Nav Dropdown - Hover interaction JavaScript
 * Handles dropdown open/close with delay for better UX
 *
 * Dropdowns are positioned as siblings outside the nav menu items,
 * linked via data-menu-item and data-dropdown attributes.
 */

(function() {
  'use strict';

  const HOVER_DELAY = 150; // ms delay before showing dropdown
  const CLOSE_DELAY = 100; // ms delay before hiding dropdown

  class DesktopNavDropdown {
    constructor() {
      this.menuItems = document.querySelectorAll('.desktop-nav-v3__menu-item--has-dropdown');
      this.dropdowns = document.querySelectorAll('.desktop-nav-v3__dropdown');
      this.openTimeout = null;
      this.closeTimeout = null;
      this.activeItem = null;

      this.init();
    }

    init() {
      if (this.menuItems.length === 0) return;

      // Menu item event listeners
      this.menuItems.forEach(item => {
        item.addEventListener('mouseenter', this.handleMouseEnter.bind(this));
        item.addEventListener('mouseleave', this.handleMouseLeave.bind(this));

        // Keyboard support
        const link = item.querySelector('.desktop-nav-v3__menu-link');
        if (link) {
          link.addEventListener('focus', this.handleFocus.bind(this));
          link.addEventListener('keydown', this.handleKeyDown.bind(this));
        }
      });

      // Dropdown hover listeners - keep dropdown open when hovering over it
      this.dropdowns.forEach(dropdown => {
        dropdown.addEventListener('mouseenter', this.handleDropdownMouseEnter.bind(this));
        dropdown.addEventListener('mouseleave', this.handleDropdownMouseLeave.bind(this));
      });

      // Close dropdown when clicking outside
      document.addEventListener('click', this.handleDocumentClick.bind(this));

      // Close dropdown on escape key
      document.addEventListener('keydown', this.handleEscapeKey.bind(this));
    }

    /**
     * Get the dropdown element for a menu item by matching data attributes
     */
    getDropdownForItem(item) {
      const menuKey = item.getAttribute('data-menu-item');
      return document.querySelector(`.desktop-nav-v3__dropdown[data-dropdown="${menuKey}"]`);
    }

    /**
     * Get the menu item element for a dropdown by matching data attributes
     */
    getMenuItemForDropdown(dropdown) {
      const dropdownKey = dropdown.getAttribute('data-dropdown');
      return document.querySelector(`.desktop-nav-v3__menu-item[data-menu-item="${dropdownKey}"]`);
    }

    handleMouseEnter(event) {
      const item = event.currentTarget;

      // Clear any pending close timeout
      if (this.closeTimeout) {
        clearTimeout(this.closeTimeout);
        this.closeTimeout = null;
      }

      // Set timeout to open dropdown
      this.openTimeout = setTimeout(() => {
        this.openDropdown(item);
      }, HOVER_DELAY);
    }

    handleMouseLeave(event) {
      const item = event.currentTarget;

      // Clear any pending open timeout
      if (this.openTimeout) {
        clearTimeout(this.openTimeout);
        this.openTimeout = null;
      }

      // Set timeout to close dropdown
      this.closeTimeout = setTimeout(() => {
        this.closeDropdown(item);
      }, CLOSE_DELAY);
    }

    handleDropdownMouseEnter(event) {
      // Clear any pending close timeout when hovering over dropdown
      if (this.closeTimeout) {
        clearTimeout(this.closeTimeout);
        this.closeTimeout = null;
      }
    }

    handleDropdownMouseLeave(event) {
      // Set timeout to close dropdown when leaving dropdown area
      if (this.activeItem) {
        this.closeTimeout = setTimeout(() => {
          this.closeDropdown(this.activeItem);
        }, CLOSE_DELAY);
      }
    }

    handleFocus(event) {
      const item = event.target.closest('.desktop-nav-v3__menu-item--has-dropdown');
      if (item) {
        this.openDropdown(item);
      }
    }

    handleKeyDown(event) {
      const item = event.target.closest('.desktop-nav-v3__menu-item--has-dropdown');

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (item.classList.contains('is-active')) {
          this.closeDropdown(item);
        } else {
          this.openDropdown(item);
        }
      }

      if (event.key === 'ArrowDown' && item.classList.contains('is-active')) {
        event.preventDefault();
        const dropdown = this.getDropdownForItem(item);
        const firstLink = dropdown?.querySelector('.desktop-nav-v3__product-card, .desktop-nav-v3__learn-link');
        if (firstLink) {
          firstLink.focus();
        }
      }
    }

    handleEscapeKey(event) {
      if (event.key === 'Escape' && this.activeItem) {
        this.closeDropdown(this.activeItem);
        const link = this.activeItem.querySelector('.desktop-nav-v3__menu-link');
        if (link) {
          link.focus();
        }
      }
    }

    handleDocumentClick(event) {
      if (!this.activeItem) return;

      const dropdown = this.getDropdownForItem(this.activeItem);
      const clickedInsideMenuItem = this.activeItem.contains(event.target);
      const clickedInsideDropdown = dropdown && dropdown.contains(event.target);

      if (!clickedInsideMenuItem && !clickedInsideDropdown) {
        this.closeDropdown(this.activeItem);
      }
    }

    openDropdown(item) {
      // Close any other open dropdowns
      if (this.activeItem && this.activeItem !== item) {
        this.closeDropdown(this.activeItem);
      }

      // Add active class to menu item
      item.classList.add('is-active');
      this.activeItem = item;

      // Add active class to corresponding dropdown
      const dropdown = this.getDropdownForItem(item);
      if (dropdown) {
        dropdown.classList.add('is-active');

        // Position narrower dropdowns (like Learn) under their menu item
        if (dropdown.getAttribute('data-dropdown') === 'learn') {
          this.positionDropdownUnderItem(item, dropdown);
        }
      }

      // Update ARIA attributes
      const link = item.querySelector('.desktop-nav-v3__menu-link');
      if (link) {
        link.setAttribute('aria-expanded', 'true');
      }
    }

    /**
     * Position a dropdown to align with its menu item
     */
    positionDropdownUnderItem(item, dropdown) {
      const itemRect = item.getBoundingClientRect();
      const headerWrapper = document.querySelector('.header-wrapper-container-nav-bar');
      const headerRect = headerWrapper ? headerWrapper.getBoundingClientRect() : { left: 0 };

      // Calculate left position relative to header wrapper
      const leftPosition = itemRect.left - headerRect.left;

      dropdown.style.left = `${leftPosition}px`;
      dropdown.style.right = 'auto';
    }

    closeDropdown(item) {
      // Remove active class from menu item
      item.classList.remove('is-active');

      // Remove active class from corresponding dropdown
      const dropdown = this.getDropdownForItem(item);
      if (dropdown) {
        dropdown.classList.remove('is-active');
      }

      if (this.activeItem === item) {
        this.activeItem = null;
      }

      // Update ARIA attributes
      const link = item.querySelector('.desktop-nav-v3__menu-link');
      if (link) {
        link.setAttribute('aria-expanded', 'false');
      }
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new DesktopNavDropdown();
    });
  } else {
    new DesktopNavDropdown();
  }
})();
